import { configureFitInputs } from './fit-guidance.ts';
import { PUBLIC_BROWSING } from './public-browsing.ts';

export type CatalogMode = 'test_fixture' | 'formal_preview';
export type InlineFixture = { id: string; name: string; note: string; color: string; accent: string; size: number; galleryUrls?: string[]; groupId?: string; displayName?: string; aliases?: string[]; filterTags?: string[]; nominalMm?: number | null; priceMinor?: number; kind?: string; mediaVersion?: string; imageUrl?: string; imageAlt?: string; available?: boolean; availableQuantity?: number };
export type AttachmentFixture = { id: string; name: string; color: string; galleryUrls?: string[]; groupId?: string; displayName?: string; nominalMm?: number | null; priceMinor?: number; kind?: string; mediaVersion?: string; imageUrl?: string; imageAlt?: string; available?: boolean; availableQuantity?: number };
export type PackagingFixture = { id: 'standard' | 'gift_box'; name: string; priceMinor: number };
export type ThemeStartingDesign = {
  id: string; name: string; note: string; inlineComponentIds: string[];
  attachment: { componentId: string; anchorIndex: number } | null;
};

// Active materials and variants are supplied only by the server catalog.
export const INLINE_FIXTURES: InlineFixture[] = [];

const LEGACY_INLINE_FIXTURES: InlineFixture[] = [
  { id: 'demo_plum_bead_8', name: 'Plum study bead', note: 'Demo color · medium', color: '#6b405d', accent: '#a97794', size: 16 },
  { id: 'demo_rose_bead_8', name: 'Rose study bead', note: 'Demo color · medium', color: '#c98286', accent: '#f0b7ae', size: 16 },
  { id: 'demo_sand_bead_8', name: 'Sand study bead', note: 'Demo color · medium', color: '#cbb38f', accent: '#eee0c6', size: 16 },
  { id: 'demo_sage_bead_8', name: 'Sage study bead', note: 'Demo color · medium', color: '#778977', accent: '#b9c4ae', size: 16 },
  { id: 'demo_gold_spacer', name: 'Warm spacer', note: 'Demo metal tone · small', color: '#b88a42', accent: '#f1d89e', size: 10 },
];

export const ATTACHMENT_FIXTURES: AttachmentFixture[] = [];
export const PACKAGING_FIXTURES: PackagingFixture[] = [];

export const THEME_STARTING_DESIGNS = new Map<string, ThemeStartingDesign>();
export const CATALOG_CONTEXT: { mode: CatalogMode; managed: boolean; version: string; priceVersion: string; assemblyMinor: number } = {
  mode: 'test_fixture', managed: false, version: '', priceVersion: '', assemblyMinor: 0,
};

const LEGACY_ATTACHMENT_FIXTURES: AttachmentFixture[] = [
  { id: 'demo_star_charm', name: 'North-star study charm', color: '#b88a42' },
];
const INACTIVE_CATALOG_INLINE: InlineFixture[] = [];
const INACTIVE_CATALOG_ATTACHMENTS: AttachmentFixture[] = [];

export const INLINE_BY_ID = new Map<string, InlineFixture>();
export const ATTACHMENT_BY_ID = new Map<string, AttachmentFixture>();
export const INLINE_IDS = new Set<string>();
export const ATTACHMENT_IDS = new Set<string>();

function rebuildFixtureIndexes() {
  INLINE_BY_ID.clear(); INLINE_IDS.clear(); ATTACHMENT_BY_ID.clear(); ATTACHMENT_IDS.clear();
  const legacyInline = CATALOG_CONTEXT.mode === 'formal_preview' ? [] : LEGACY_INLINE_FIXTURES;
  const legacyAttachments = CATALOG_CONTEXT.mode === 'formal_preview' ? [] : LEGACY_ATTACHMENT_FIXTURES;
  for (const fixture of [...INLINE_FIXTURES, ...INACTIVE_CATALOG_INLINE, ...legacyInline]) { INLINE_BY_ID.set(fixture.id, fixture); INLINE_IDS.add(fixture.id); }
  for (const fixture of [...ATTACHMENT_FIXTURES, ...INACTIVE_CATALOG_ATTACHMENTS, ...legacyAttachments]) { ATTACHMENT_BY_ID.set(fixture.id, fixture); ATTACHMENT_IDS.add(fixture.id); }
}

function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value); }
function isHex(value: unknown): value is string { return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value); }
function money(minor: number): string { return `$${(minor / 100).toFixed(2)}`; }

export function configureCatalog(value: unknown): boolean {
  if (!isRecord(value) || (value.catalog_mode !== 'test_fixture' && value.catalog_mode !== 'formal_preview')
    || typeof value.catalog_version !== 'string' || typeof value.price_version !== 'string'
    || !Array.isArray(value.components) || !Array.isArray(value.packaging)) return false;
  const mode = value.catalog_mode as CatalogMode;
  const label = mode === 'formal_preview' ? 'Formal preview' : 'TEST';
  const inline: InlineFixture[] = [];
  const attachments: AttachmentFixture[] = [];
  const inactiveInline: InlineFixture[] = [];
  const inactiveAttachments: AttachmentFixture[] = [];
  for (const raw of value.components) {
    if (!isRecord(raw) || typeof raw.sku !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(raw.sku)
      || typeof raw.name !== 'string' || typeof raw.kind !== 'string' || !Number.isSafeInteger(raw.unit_price_minor)
      || (raw.unit_price_minor as number) < 0 || !isHex(raw.color) || !isHex(raw.accent)
      || !Number.isSafeInteger(raw.available_quantity) || (raw.available_quantity as number) < 0
      || (raw.nominal_size_mm !== null && (typeof raw.nominal_size_mm !== 'number' || !Number.isFinite(raw.nominal_size_mm) || raw.nominal_size_mm <= 0 || raw.nominal_size_mm > 99999.999))) return false;
    const imageUrl = typeof raw.image_url === 'string' && /^(?:https?:)?\//.test(raw.image_url) ? raw.image_url : undefined;
    const imageAlt = typeof raw.image_alt === 'string' ? raw.image_alt : `${raw.name} — ${label} catalog image`;
    const active = raw.active !== false;
    const availableQuantity = raw.available_quantity as number;
    const available = active && availableQuantity > 0 && (mode === 'test_fixture' || raw.media_ready === true);
    if (raw.kind === 'charm') {
      (active ? attachments : inactiveAttachments).push({ id: raw.sku, name: PUBLIC_BROWSING ? raw.name : `${raw.name} · ${label} · ${money(raw.unit_price_minor as number)}`, mediaVersion: typeof raw.media_version === 'string' ? raw.media_version : '', priceMinor: raw.unit_price_minor as number, color: raw.color, imageUrl, imageAlt, available, availableQuantity });
    } else if (raw.kind === 'bead' || raw.kind === 'spacer' || raw.kind === 'connector') {
      const nominal = raw.nominal_size_mm as number | null;
      const size = nominal === null ? 10 : nominal * 16 / 6;
      (active ? inline : inactiveInline).push({
        id: raw.sku, name: PUBLIC_BROWSING ? raw.name : `${raw.name} · ${label}`,
        note: `${nominal === null ? 'Size unknown' : `Nominal ${nominal}mm`} · ${money(raw.unit_price_minor as number)}${available ? '' : ' · unavailable'}`,
        galleryUrls: Array.isArray(raw.gallery_urls) ? raw.gallery_urls.filter((url):url is string => typeof url === 'string' && /^https?:\/\//.test(url)) : [], groupId: Number.isSafeInteger(raw.material_id) && (raw.material_id as number) > 0 ? `material:${raw.material_id}` : (typeof raw.stone_group === 'string' && raw.stone_group ? raw.stone_group : raw.sku), displayName: typeof raw.material_name === 'string' && raw.material_name.trim() ? raw.material_name : raw.name,
        aliases: Array.isArray(raw.aliases) ? raw.aliases.filter((alias): alias is string => typeof alias === 'string').map((alias) => alias.trim()).filter(Boolean) : [],
        filterTags: Array.isArray(raw.themes) ? raw.themes.filter((theme): theme is string => typeof theme === 'string' && theme !== '') : [],
        nominalMm: nominal, priceMinor: raw.unit_price_minor as number, kind: raw.kind, mediaVersion: typeof raw.media_version === 'string' ? raw.media_version : '', color: raw.color, accent: raw.accent, size, imageUrl, imageAlt, available, availableQuantity,
      });
    } else return false;
  }
  const packaging: PackagingFixture[] = [];
  for (const raw of value.packaging) {
    if (!isRecord(raw) || (raw.id !== 'standard' && raw.id !== 'gift_box') || typeof raw.name !== 'string'
      || !Number.isSafeInteger(raw.price_minor) || (raw.price_minor as number) < 0) return false;
    packaging.push({ id: raw.id, name: raw.name, priceMinor: raw.price_minor as number });
  }

  const themeDesigns = new Map<string, ThemeStartingDesign>();
  if (isRecord(value.theme_templates)) {
    for (const [theme, raw] of Object.entries(value.theme_templates)) {
      if (!isRecord(raw) || typeof raw.id !== 'string' || typeof raw.name !== 'string' || typeof raw.note !== 'string'
        || !Array.isArray(raw.inline_component_ids) || raw.inline_component_ids.length === 0
        || raw.inline_component_ids.some((id) => typeof id !== 'string' || !inline.some((fixture) => fixture.id === id))) continue;
      let attachment: ThemeStartingDesign['attachment'] = null;
      if (raw.attachment !== null) {
        const candidate = raw.attachment;
        if (!isRecord(candidate) || typeof candidate.component_id !== 'string' || typeof candidate.anchor_index !== 'number'
          || !attachments.some((fixture) => fixture.id === candidate.component_id)
          || !Number.isInteger(candidate.anchor_index) || candidate.anchor_index < 0
          || candidate.anchor_index >= raw.inline_component_ids.length) continue;
        attachment = { componentId: candidate.component_id, anchorIndex: candidate.anchor_index };
      }
      themeDesigns.set(theme, { id: raw.id, name: raw.name, note: raw.note, inlineComponentIds: raw.inline_component_ids as string[], attachment });
    }
  }

  INLINE_FIXTURES.splice(0, INLINE_FIXTURES.length, ...inline);
  ATTACHMENT_FIXTURES.splice(0, ATTACHMENT_FIXTURES.length, ...attachments);
  INACTIVE_CATALOG_INLINE.splice(0, INACTIVE_CATALOG_INLINE.length, ...inactiveInline);
  INACTIVE_CATALOG_ATTACHMENTS.splice(0, INACTIVE_CATALOG_ATTACHMENTS.length, ...inactiveAttachments);
  PACKAGING_FIXTURES.splice(0, PACKAGING_FIXTURES.length, ...packaging);
  configureFitInputs(value.fit_guidance);
  CATALOG_CONTEXT.mode = mode;
  CATALOG_CONTEXT.managed = true;
  CATALOG_CONTEXT.version = value.catalog_version;
  CATALOG_CONTEXT.priceVersion = value.price_version;
  CATALOG_CONTEXT.assemblyMinor = Number.isSafeInteger(value.assembly_minor) ? value.assembly_minor as number : 0;
  THEME_STARTING_DESIGNS.clear(); for (const [theme, template] of themeDesigns) THEME_STARTING_DESIGNS.set(theme, template);
  rebuildFixtureIndexes();
  return true;
}

rebuildFixtureIndexes();
